/**
 * AlertService.ts
 *
 * API client for the L4 intelligence alert system.
 * Provides access to alert CRUD, acknowledgment workflows,
 * snooze/resolve actions, and alert rule configuration.
 */

import { http } from './api';

// ====== Response Types ======

export interface IntelligenceAlertResponse {
  id: string;
  tenant_id: string;
  alert_type: string;  // hot_prospect, win_rate_decline, competitor_entry, churn_risk, pipeline_stall
  severity: string;    // critical, high, medium, low
  title: string;
  message: string;
  data: Record<string, unknown>;
  status: string;      // new, acknowledged, snoozed, resolved
  snoozed_until: string | null;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  channel: string;
  delivered_at: string | null;
  created_at: string;
}

export interface AlertRuleResponse {
  id: string;
  tenant_id: string;
  rule_type: string;
  enabled: boolean;
  threshold: Record<string, unknown>;
  channels: string[];
  cooldown_minutes: number;
  recipients: string[];
  created_by: string | null;
  created_at: string;
}

export interface AlertStats {
  total_new: number;
  by_severity: Record<string, number>;
  by_type: Record<string, number>;
}

// ====== Request Types ======

export interface ListAlertsParams {
  alert_type?: string;
  severity?: string;
  status?: string;
  offset?: number;
  limit?: number;
}

export interface UpsertRuleRequest {
  rule_type: string;
  enabled: boolean;
  threshold: Record<string, unknown>;
  channels: string[];
  cooldown_minutes: number;
  recipients?: string[];
}

// ====== Service ======

class AlertService {
  /**
   * List alerts with optional filters
   */
  async listAlerts(params?: ListAlertsParams): Promise<{ alerts: IntelligenceAlertResponse[]; total: number }> {
    return http('/alerts', { query: params as Record<string, string | number | boolean | undefined> });
  }

  /**
   * Get aggregate alert statistics (counts by severity, type)
   */
  async getStats(): Promise<AlertStats> {
    return http('/alerts/stats');
  }

  /**
   * Acknowledge an alert (marks it as seen by current user)
   */
  async acknowledge(id: string): Promise<IntelligenceAlertResponse> {
    return http(`/alerts/${id}/acknowledge`, { method: 'POST' });
  }

  /**
   * Snooze an alert for a given number of hours
   */
  async snooze(id: string, hours: number): Promise<IntelligenceAlertResponse> {
    return http(`/alerts/${id}/snooze`, {
      method: 'POST',
      body: JSON.stringify({ hours }),
    });
  }

  /**
   * Resolve an alert (mark as handled)
   */
  async resolve(id: string): Promise<IntelligenceAlertResponse> {
    return http(`/alerts/${id}/resolve`, { method: 'POST' });
  }

  /**
   * List all alert rules for the current tenant
   */
  async listRules(): Promise<{ rules: AlertRuleResponse[] }> {
    return http('/alerts/rules');
  }

  /**
   * Create or update an alert rule (upsert by rule_type)
   */
  async upsertRule(rule: UpsertRuleRequest): Promise<AlertRuleResponse> {
    return http('/alerts/rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    });
  }

  /**
   * Delete an alert rule by ID
   */
  async deleteRule(id: string): Promise<void> {
    return http(`/alerts/rules/${id}`, { method: 'DELETE' });
  }
}

export default new AlertService();
